import { useMemo, useState } from 'react';
import { Bevestig, Kaart, Knop, Leeg, PaginaKop, Paginering, SkeletTabel, useToast } from '../components/ui';
import { useAuth } from '../lib/auth';
import { fmtDatumTijd } from '../lib/format';
import { useLogboek, useProfiles, useSchoonLogboekOp } from '../lib/queries';
import type { LogRegel } from '../lib/types';

const SOORT_TOON: Record<LogRegel['soort'], string> = {
  inlog: 'bg-veld-50 text-veld-800 border-veld-200',
  bewerking: 'bg-krijt-100 text-krijt-700 border-krijt-300',
  systeem: 'bg-amber-50 text-amber-800 border-amber-200'
};

export default function Log() {
  const { demo } = useAuth();
  const logboek = useLogboek();
  const profielen = useProfiles();
  const opschonen = useSchoonLogboekOp();
  const meld = useToast();

  const [zoek, setZoek] = useState('');
  const [soort, setSoort] = useState('');
  const [gebruiker, setGebruiker] = useState('');
  const [datum, setDatum] = useState('');
  const [wilOpschonen, setWilOpschonen] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [perPagina, setPerPagina] = useState(25);

  const naamVan = useMemo(() => {
    const kaart = new Map((profielen.data ?? []).map((p) => [p.id, p.name]));
    return (id: string | null) => (id ? (kaart.get(id) ?? 'onbekend') : 'systeem');
  }, [profielen.data]);

  const alles = (logboek.data ?? []).filter((r) => {
    if (soort && r.soort !== soort) return false;
    if (gebruiker && r.profielId !== gebruiker) return false;
    if (datum && !r.moment.startsWith(datum)) return false;
    if (zoek && !r.omschrijving.toLowerCase().includes(zoek.trim().toLowerCase())) return false;
    return true;
  });

  /* Filteren gooit de bladzijde om; anders sta je opeens op een lege pagina. */
  const paginas = Math.max(1, Math.ceil(alles.length / perPagina));
  const huidige = Math.min(pagina, paginas - 1);
  const rijen = alles.slice(huidige * perPagina, (huidige + 1) * perPagina);

  if (demo) {
    return (
      <>
        <PaginaKop titel="Logs" />
        <Kaart>
          <Leeg tekst="In demomodus wordt er niets gelogd; er is geen database." ikoon="blad" />
        </Kaart>
      </>
    );
  }

  return (
    <>
      <PaginaKop
        titel="Logs"
        onder="Bewust zuinig: inloggen, uitloggen en wijzigingen die ertoe doen. Geen paginabezoeken."
        rechts={
          <Knop variant="gevaar" onClick={() => setWilOpschonen(true)}>
            Logs opschonen
          </Knop>
        }
      />

      <Kaart>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            className="veld w-auto min-w-52 flex-1"
            placeholder="Zoeken in logtekst"
            value={zoek}
            onChange={(e) => {
              setZoek(e.target.value);
              setPagina(0);
            }}
          />
          <select className="veld w-auto" value={soort} onChange={(e) => {
              setSoort(e.target.value);
              setPagina(0);
            }}>
            <option value="">Alle types</option>
            <option value="inlog">Inlog</option>
            <option value="bewerking">Bewerking</option>
            <option value="systeem">Systeem</option>
          </select>
          <select className="veld w-auto" value={gebruiker} onChange={(e) => {
              setGebruiker(e.target.value);
              setPagina(0);
            }}>
            <option value="">Alle gebruikers</option>
            {(profielen.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <input type="date" className="veld w-auto" value={datum} onChange={(e) => {
              setDatum(e.target.value);
              setPagina(0);
            }} />
        </div>

        {logboek.isLoading ? (
          <SkeletTabel rijen={10} kolommen={4} />
        ) : rijen.length ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-y-2 border-krijt-900 bg-krijt-50">
                  {['Moment', 'Type', 'Gebruiker', 'Omschrijving'].map((k) => (
                    <th key={k} className="opschrift whitespace-nowrap px-3 py-2.5 text-left text-krijt-600">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rijen.map((r) => (
                  <tr key={r.id} className="border-b border-krijt-200 hover:bg-krijt-50">
                    <td className="nums whitespace-nowrap px-3 py-2.5 text-krijt-600">{fmtDatumTijd(r.moment)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex border px-2 py-0.5 text-[11.5px] font-semibold ${SOORT_TOON[r.soort]}`}>
                        {r.soort}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">{naamVan(r.profielId)}</td>
                    <td className="px-3 py-2.5 text-krijt-700">{r.omschrijving}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Leeg tekst="Geen logregels die aan dit filter voldoen." ikoon="zoek" />
        )}

        <Paginering
          totaal={alles.length}
          perPagina={perPagina}
          pagina={huidige}
          onPagina={setPagina}
          onPerPagina={(n) => {
            setPerPagina(n);
            setPagina(0);
          }}
        />

        <p className="mt-3 text-xs text-krijt-500">
          De laatste 300 regels. Opgeschoonde regels tellen ook niet meer mee op de Gebruik-pagina.
        </p>
      </Kaart>

      {wilOpschonen ? (
        <Bevestig
          titel="Logs opschonen?"
          tekst="Alles ouder dan 90 dagen wordt gewist. Dat is niet terug te draaien, en de grafieken op Gebruik worden er korter van."
          knop="Opschonen"
          onNee={() => setWilOpschonen(false)}
          onJa={async () => {
            try {
              const weg = await opschonen.mutateAsync(90);
              meld(`${weg} logregels opgeruimd.`);
            } catch (err) {
              meld(err instanceof Error ? err.message : 'Opschonen mislukte.', true);
            }
            setWilOpschonen(false);
          }}
        />
      ) : null}
    </>
  );
}
